import React, { useState, useEffect, useRef } from 'react'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  MagnifyingGlassIcon,
  // FunnelIcon,
  TagIcon,
  StarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PhotoIcon,
  AdjustmentsHorizontalIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { useCategories } from '../contexts/CategoryContext'
import { apiService, resolveImageUrl } from '../services/api'
import { compareProductCode } from '../utils/productCodeSort'

/** Normalize image for API: store path only so DB stays consistent; full URLs are stripped to pathname. */
function imageToApiValue(value: string | undefined | null): string | null {
  if (value == null || value === '') return null
  try {
    if (value.startsWith('http://') || value.startsWith('https://')) return new URL(value).pathname
  } catch (_) {}
  return value
}

interface RestaurantSettings {
  restaurant_name: string
  phone_number: string
  address: string
  email: string
  website: string
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
  timezone: string
  language: string
  tax_rate: number
  service_charge: number
}

interface KDSStation {
  id: string
  name: string
  description?: string
  color: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

interface MenuItem {
  id: string
  code?: string
  name: string
  nameAr?: string
  description: string
  descriptionAr?: string
  category: string
  subcategory?: string
  basePrice: number
  image?: string
  isActive: boolean
  isPopular: boolean
  preparationTime: number
  calories?: number
  stepsToBurn?: number
  allergens?: string[]
  tags: string[]
  variants?: MenuVariant[]
  addons?: Addon[]
  upsells?: Upsell[]
  crossSells?: CrossSell[]
  kdsStationId?: string
  createdAt: Date
  updatedAt: Date
}

interface MenuVariant {
  id: string
  name: string
  priceModifier: number
  isDefault: boolean
  allergen_ids?: string[]
}

interface Addon {
  id: string
  name: string
  price: number
  category: string
  isRequired: boolean
  maxSelection?: number
  kdsStationId?: string
  allergen_ids?: string[]
}

interface Upsell {
  id: string
  name: string
  discount: number
  triggerAmount: number
}

interface CrossSell {
  id: string
  name: string
  discount: number
  relatedItems: string[]
}

interface Offer {
  id: string
  name: string
  description: string
  type: 'percentage' | 'fixed' | 'bogo' | 'combo'
  value: number
  startDate: Date
  endDate: Date
  isActive: boolean
  applicableItems: string[] // For percentage/fixed discounts
  comboItems?: ComboItem[] // For combo deals
  bogoItems?: BOGOItem[] // For BOGO deals
  minOrderAmount?: number
  maxDiscount?: number
  usageLimit?: number
  usedCount: number
}

interface ComboItem {
  id: string
  name: string
  price: number
}

interface BOGOItem {
  buyItemId: string
  buyItemName: string
  getItemId: string
  getItemName: string
  discountType: 'free' | 'percentage' | 'fixed'
  discountValue: number
}

// Helpers for category filter (include selected category and all descendants)
function getDescendantIds(cat: { id: string; children?: Array<{ id: string; children?: any[] }> }): string[] {
  const ids: string[] = []
  if (cat.children?.length) {
    for (const child of cat.children) {
      ids.push(child.id, ...getDescendantIds(child))
    }
  }
  return ids
}
function getCategoryAndDescendantIds(categoryId: string, categories: Array<{ id: string; children?: any[] }>): string[] {
  const normalizedId = String(categoryId).trim()
  for (const cat of categories) {
    if (String(cat.id).trim() === normalizedId) {
      return [String(cat.id), ...getDescendantIds(cat)]
    }
    if (cat.children?.length) {
      const found = getCategoryAndDescendantIds(categoryId, cat.children)
      if (found.length > 0) return found
    }
  }
  return []
}

const MenuManagement: React.FC = () => {
  const { categories, getAllCategoriesFlat } = useCategories()
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [menuItemsLoading, setMenuItemsLoading] = useState(true)
  const [offers, setOffers] = useState<Offer[]>([])
  const [activeTab, setActiveTab] = useState<'items' | 'offers' | 'upsells' | 'addonCategories'>('items')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddonModal, setShowAddonModal] = useState(false)
  const [showOfferModal, setShowOfferModal] = useState(false)
  const [showViewOfferModal, setShowViewOfferModal] = useState(false)
  const [showEditOfferModal, setShowEditOfferModal] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null)
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [editingVariant, setEditingVariant] = useState<MenuVariant | null>(null)
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const editModalOriginalImageRef = useRef<string | undefined>(undefined)
  const [imagePreviewBlobUrl, setImagePreviewBlobUrl] = useState<string | null>(null)
  const [editingAddon, setEditingAddon] = useState<Addon | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [filterActiveStatus, setFilterActiveStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [sortBy, setSortBy] = useState<'default' | 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc'>('default')
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const initialNewItem: Partial<MenuItem> = {
    code: '',
    name: '',
    description: '',
    category: '',
    subcategory: '',
    basePrice: undefined,
    image: '',
    isActive: true,
    isPopular: false,
    preparationTime: 10,
    calories: undefined,
    stepsToBurn: undefined,
    allergens: [],
    tags: [],
    variants: [],
    addons: [],
    upsells: [],
    crossSells: [],
    kdsStationId: undefined
  }
  const [newItem, setNewItem] = useState<Partial<MenuItem>>(initialNewItem)
  const [newAddon, setNewAddon] = useState<Partial<Addon>>({})
  const [addonCategories, setAddonCategories] = useState<Array<{ id: string; name: string; is_active: boolean; sort_order: number }>>([])

  useEffect(() => {
    if (!showEditModal && !showAddModal && imagePreviewBlobUrl) {
      URL.revokeObjectURL(imagePreviewBlobUrl)
      setImagePreviewBlobUrl(null)
    }
  }, [showEditModal, showAddModal])

  const [showAddAddonCategoryModal, setShowAddAddonCategoryModal] = useState(false)
  const [newAddonCategoryName, setNewAddonCategoryName] = useState('')
  const [newOffer, setNewOffer] = useState<Partial<Offer>>({})
  const [newVariant, setNewVariant] = useState<Partial<MenuVariant>>({})
  const [selectedOfferType, setSelectedOfferType] = useState<'percentage' | 'fixed' | 'bogo' | 'combo'>('percentage')
  const [selectedMenuItems, setSelectedMenuItems] = useState<string[]>([])
  const [comboItems, setComboItems] = useState<ComboItem[]>([])
  const [bogoItems, setBogoItems] = useState<BOGOItem[]>([])
  // const [showItemSelector, setShowItemSelector] = useState(false)
  
  // Restaurant Settings State
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    restaurant_name: 'Mufaza Restaurant',
    phone_number: '+966 11 123 4567',
    address: '123 King Fahd Road, Riyadh, Saudi Arabia',
    email: 'info@mufaza.com',
    website: 'www.mufaza.com',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: '',
    timezone: 'Asia/Riyadh',
    language: 'English',
    tax_rate: 15,
    service_charge: 10
  })

  // KDS Stations State
  const [kdsStations, setKdsStations] = useState<KDSStation[]>([])
  
  // Recipe Management State
  const [posSettings, setPOSSettings] = useState<any>({ linkInventoryWithProducts: false })
  const [inventoryItems, setInventoryItems] = useState<any[]>([])
  const [showRecipeModal, setShowRecipeModal] = useState(false)
  const [recipeTarget, setRecipeTarget] = useState<{ type: 'item' | 'variant' | 'addon', menuItemId: string, variantId?: string, addonId?: string } | null>(null)
  const [recipeIngredients, setRecipeIngredients] = useState<Array<{ inventory_item_id: string, quantity: number, unit: string, notes?: string }>>([])
  const [recipeServingSize, setRecipeServingSize] = useState<number>(1)
  const [recipeName, setRecipeName] = useState<string>('')
  const [recipeDescription, setRecipeDescription] = useState<string>('')

  // Allergens list (for multi-select in item/variant/addon forms)
  const [allergensList, setAllergensList] = useState<Array<{ id: string; name: string; icon: string | null; sort_order: number }>>([])

  // Delete menu item modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<MenuItem | null>(null)
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false)

  // Offer update success modal
  const [showOfferUpdateSuccessModal, setShowOfferUpdateSuccessModal] = useState(false)

  // Reusable alert/confirm modal (replaces alert() and confirm())
  const [alertModal, setAlertModal] = useState<{
    open: boolean
    title: string
    message: string
    variant: 'success' | 'error' | 'info' | 'confirm'
    confirmOnly: boolean
    onConfirm?: () => void
  }>({ open: false, title: '', message: '', variant: 'info', confirmOnly: true })

  const showAlert = (title: string, message: string, variant: 'success' | 'error' | 'info' = 'info') => {
    setAlertModal({ open: true, title, message, variant, confirmOnly: true })
  }
  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setAlertModal({ open: true, title, message, variant: 'confirm', confirmOnly: false, onConfirm })
  }
  const closeAlertModal = () => setAlertModal(prev => ({ ...prev, open: false }))
  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) alertModal.onConfirm()
    closeAlertModal()
  }

  // Format offer value for display based on type
  const formatOfferValue = (offer: any): string => {
    if (!offer) return '-'
    if (offer.type === 'percentage') return `${Number(offer.value || 0)}%`
    if (offer.type === 'fixed') {
      const amount = Number(offer.value || 0).toFixed(2)
      if (restaurantSettings.currency_display_position === 'left') {
        return `${restaurantSettings.currency_symbol}${amount}`
      } else {
        return `${amount}${restaurantSettings.currency_symbol}`
      }
    }
    if (offer.type === 'combo') {
      const items = offer.comboItems || offer.combo_items || []
      const total = items.reduce((sum: number, it: any) => sum + Number(it?.price || 0), 0)
      const amount = total.toFixed(2)
      if (restaurantSettings.currency_display_position === 'left') {
        return `${restaurantSettings.currency_symbol}${amount}`
      } else {
        return `${amount}${restaurantSettings.currency_symbol}`
      }
    }
    if (offer.type === 'bogo') {
      const rules = offer.bogoItems || offer.bogo_items || []
      if (!rules.length) return '—'
      if (rules.length > 1) return 'Multiple rules'
      const r = rules[0]
      if (r.discountType === 'free') return 'Free item'
      if (r.discountType === 'percentage') return `${Number(r.discountValue || 0)}%`
      const amount = Number(r.discountValue || 0).toFixed(2)
      if (restaurantSettings.currency_display_position === 'left') {
        return `${restaurantSettings.currency_symbol}${amount}`
      } else {
        return `${amount}${restaurantSettings.currency_symbol}`
      }
    }
    return '-'
  }

  // Load restaurant settings from database
  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        setRestaurantSettings(response.data as unknown as RestaurantSettings)
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  // Load KDS stations from database
  const loadKDSStations = async () => {
    try {
      const response = await apiService.getKDSStations()
      if (response.success && response.data) {
        setKdsStations((response.data as KDSStation[]).filter((station: KDSStation) => station.is_active))
      }
    } catch (error) {
      console.error('Error loading KDS stations:', error)
    }
  }

  // Helper function to format currency with display options
  const formatCurrency = (amount: number | string | null | undefined) => {
    // Convert to number if it's a string or handle null/undefined
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0)
    if (isNaN(numAmount)) {
      return '0.00'
    }
    const formattedAmount = numAmount.toFixed(2)
    
    if (restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url) {
      // Use PNG icon
      if (restaurantSettings.currency_display_position === 'left') {
        return (
          <>
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 mr-1" />
            {formattedAmount}
          </>
        )
      } else {
        return (
          <>
            {formattedAmount}
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 ml-1" />
          </>
        )
      }
    } else {
      // Use text symbol
      if (restaurantSettings.currency_display_position === 'left') {
        return `${restaurantSettings.currency_symbol}${formattedAmount}`
      } else {
        return `${formattedAmount}${restaurantSettings.currency_symbol}`
      }
    }
  }

  // Load POS settings
  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()
      if (response.success && response.data) {
        setPOSSettings(response.data)
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  // Load inventory items for recipe management
  const loadInventoryItems = async () => {
    try {
      const response = await apiService.getInventory()
      if (response.success && response.data) {
        setInventoryItems(Array.isArray(response.data) ? (response.data as any[]) : [])
      }
    } catch (error) {
      console.error('Error loading inventory items:', error)
    }
  }

  // Load recipe for a menu item, variant, or addon
  const loadRecipe = async (menuItemId: string, variantId?: string, addonId?: string) => {
    try {
      const response = await apiService.getRecipes(menuItemId, variantId, addonId)
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const recipe = (response.data as any[])[0]
        setRecipeName(recipe.recipe_name || '')
        setRecipeDescription(recipe.description || '')
        setRecipeServingSize(recipe.serving_size || 1)
        if (recipe.recipe_ingredients && recipe.recipe_ingredients.length > 0) {
          setRecipeIngredients(recipe.recipe_ingredients.map((ing: any) => ({
            inventory_item_id: ing.inventory_item_id,
            quantity: ing.quantity || 1,
            unit: ing.unit || '',
            notes: ing.notes || ''
          })))
        } else {
          setRecipeIngredients([])
        }
      } else {
        // No recipe found, reset to defaults
        setRecipeName('')
        setRecipeDescription('')
        setRecipeServingSize(1)
        setRecipeIngredients([])
      }
    } catch (error) {
      console.error('Error loading recipe:', error)
      setRecipeName('')
      setRecipeDescription('')
      setRecipeServingSize(1)
      setRecipeIngredients([])
    }
  }

  // Save recipe
  const saveRecipe = async () => {
    if (!recipeTarget) return
    
    try {
      const recipeData = {
        menu_item_id: recipeTarget.menuItemId,
        variant_id: recipeTarget.variantId || null,
        addon_id: recipeTarget.addonId || null,
        recipe_name: recipeName || null,
        description: recipeDescription || null,
        serving_size: recipeServingSize || 1,
        ingredients: recipeIngredients
      }
      
      const response = await apiService.createRecipe(recipeData)
      if (response.success) {
        showAlert('Recipe Saved', 'Recipe saved successfully.', 'success')
        setShowRecipeModal(false)
      } else {
        showAlert('Error', response.error || 'Failed to save recipe', 'error')
      }
    } catch (error) {
      console.error('Error saving recipe:', error)
      showAlert('Error', 'Failed to save recipe', 'error')
    }
  }

  // Handle manage recipe button click
  const handleManageRecipe = async (type: 'item' | 'variant' | 'addon', menuItemId: string, variantId?: string, addonId?: string) => {
    setRecipeTarget({ type, menuItemId, variantId, addonId })
    await loadRecipe(menuItemId, variantId, addonId)
    setShowRecipeModal(true)
  }

  // Add ingredient to recipe
  const handleAddIngredient = () => {
    setRecipeIngredients([...recipeIngredients, { inventory_item_id: '', quantity: 1, unit: '', notes: '' }])
  }

  // Remove ingredient from recipe
  const handleRemoveIngredient = (index: number) => {
    setRecipeIngredients(recipeIngredients.filter((_, i) => i !== index))
  }

  // Update ingredient in recipe
  const handleUpdateIngredient = (index: number, field: string, value: any) => {
    const updated = [...recipeIngredients]
    updated[index] = { ...updated[index], [field]: value }
    setRecipeIngredients(updated)
  }

  useEffect(() => {
    const loadAllergens = async () => {
      try {
        const res = await apiService.getAllergens()
        if (res.success && res.data) setAllergensList(Array.isArray(res.data) ? res.data : [])
      } catch (_) {}
    }
    const loadData = async () => {
      const [offerData, addonCategoryData] = await Promise.all([
        loadOffers(),
        loadAddonCategories(),
        loadRestaurantSettings(),
        loadKDSStations(),
        loadPOSSettings(),
        loadInventoryItems(),
        loadMenuData(),
        loadAllergens()
      ])
      setOffers(offerData)
      setAddonCategories(addonCategoryData as Array<{ id: string; name: string; is_active: boolean; sort_order: number }>)
    }
    loadData()
  }, [])


  const loadOffers = async () => {
    try {
      const response = await apiService.getOffers()
      if (response.success && response.data) {
        // Transform API response to match frontend format
        const transformedOffers = (Array.isArray(response.data) ? response.data : []).map((offer: any) => ({
          ...offer,
          isActive: offer.is_active, // Convert is_active to isActive
          startDate: new Date(offer.start_date), // Convert start_date to startDate
          endDate: new Date(offer.end_date), // Convert end_date to endDate
          minOrderAmount: offer.min_order_amount, // Convert min_order_amount to minOrderAmount
          maxDiscount: offer.max_discount, // Convert max_discount to maxDiscount
          usageLimit: offer.usage_limit, // Convert usage_limit to usageLimit
          usedCount: offer.used_count, // Convert used_count to usedCount
          applicableItems: offer.applicable_items, // Convert applicable_items to applicableItems
          comboItems: offer.combo_items || [], // Map combo_items -> comboItems
          bogoItems: offer.bogo_items || [], // Map bogo_items -> bogoItems
          createdAt: offer.created_at, // Convert created_at to createdAt
          updatedAt: offer.updated_at, // Convert updated_at to updatedAt
        }))
        return transformedOffers
      } else {

        // Fallback to mock data if API fails (table doesn't exist yet)
        return loadMockOffers()
      }
    } catch (error) {

      // Fallback to mock data if API fails (table doesn't exist yet)
      return loadMockOffers()
    }
  }

  const loadAddonCategories = async () => {
    try {
      const response = await apiService.getAddonCategories()
      if (response.success && response.data) {
        return response.data
      }
      return []
    } catch (e) {
      console.error('Error loading addon categories:', e)
      return []
    }
  }

  const loadMockOffers = () => {
    // Mock offers data for when database table doesn't exist yet
    const mockOffers: Offer[] = [
      {
        id: 'o1',
        name: 'Happy Hour Special',
        description: '20% off all drinks during happy hour',
        type: 'percentage',
        value: 20,
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isActive: true,
        applicableItems: ['1'],
        minOrderAmount: 25,
        maxDiscount: 10,
        usageLimit: 100,
        usedCount: 15
      },
      {
        id: 'o2',
        name: 'Buy One Get One',
        description: 'Buy one chicken shawarma, get one half price',
        type: 'bogo',
        value: 50,
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isActive: true,
        applicableItems: ['1'],
        usageLimit: 50,
        usedCount: 8
      }
    ]
    return mockOffers
  }

  const loadMenuData = async () => {
    setMenuItemsLoading(true)
    try {
      const response = await apiService.getMenuItems()
      if (response.success && response.data) {
        const transformedItems = (Array.isArray(response.data) ? response.data : []).map((item: any) => ({
          ...item,
          image: resolveImageUrl(item.image),
          code: item.code ?? '',
          nameAr: item.name_ar || undefined,
          descriptionAr: item.description_ar || undefined,
          category: item.category_id,
          basePrice: item.base_price,
          preparationTime: item.preparation_time,
          calories: item.calories,
          stepsToBurn: item.steps_to_burn,
          isActive: item.is_active,
          isPopular: item.is_popular,
          kdsStationId: item.kds_station_id,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
        }))
        setMenuItems(transformedItems)
      } else {
        console.error('Failed to load menu items:', response.error)
        loadMockData()
      }
    } catch (error) {
      console.error('Error loading menu items:', error)
      loadMockData()
    } finally {
      setMenuItemsLoading(false)
    }
  }

  const loadMockData = () => {
    // Mock menu items with comprehensive data
    const mockMenuItems: MenuItem[] = [
      {
        id: '550e8400-e29b-41d4-a716-446655440101',
        name: 'Chicken Shawarma',
        description: 'Tender chicken with fresh vegetables and tahini sauce',
        category: '550e8400-e29b-41d4-a716-446655440011', // Grilled subcategory UUID
        subcategory: 'Grilled',
        basePrice: 15.00,
        isActive: true,
        isPopular: true,
        preparationTime: 15,
        calories: 450,
        allergens: ['sesame'],
        tags: ['popular', 'grilled', 'chicken'],
        variants: [
          { id: 'v1', name: 'Regular', priceModifier: 0, isDefault: true },
          { id: 'v2', name: 'Large', priceModifier: 5, isDefault: false },
          { id: 'v3', name: 'Extra Large', priceModifier: 8, isDefault: false }
        ],
        addons: [
          { id: 'a1', name: 'Extra Chicken', price: 3, category: 'Protein', isRequired: false },
          { id: 'a2', name: 'Extra Sauce', price: 1, category: 'Sauce', isRequired: false },
          { id: 'a3', name: 'Extra Vegetables', price: 2, category: 'Vegetables', isRequired: false }
        ],
        upsells: [
          { id: 'u1', name: 'Add Fries', discount: 2, triggerAmount: 15 }
        ],
        crossSells: [
          { id: 'c1', name: 'Combo with Drink', discount: 3, relatedItems: ['drink'] }
        ],
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '550e8400-e29b-41d4-a716-446655440102',
        name: 'Hummus Plate',
        description: 'Creamy hummus served with pita bread and olive oil',
        category: '550e8400-e29b-41d4-a716-446655440021', // Dips & Spreads subcategory UUID
        subcategory: 'Dips',
        basePrice: 8.00,
        isActive: true,
        isPopular: false,
        preparationTime: 5,
        calories: 280,
        allergens: ['sesame'],
        tags: ['vegetarian', 'dip'],
        variants: [
          { id: 'v4', name: 'Regular', priceModifier: 0, isDefault: true },
          { id: 'v5', name: 'Family Size', priceModifier: 4, isDefault: false }
        ],
        addons: [
          { id: 'a4', name: 'Extra Pita', price: 2, category: 'Bread', isRequired: false },
          { id: 'a5', name: 'Olives', price: 1.5, category: 'Toppings', isRequired: false }
        ],
        upsells: [],
        crossSells: [],
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ]

    const mockOffers: Offer[] = [
      {
        id: 'o1',
        name: 'Weekend Special',
        description: '20% off all main dishes on weekends',
        type: 'percentage',
        value: 20,
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        isActive: true,
        applicableItems: ['1', '2'],
        minOrderAmount: 25,
        maxDiscount: 10,
        usageLimit: 100,
        usedCount: 15
      },
      {
        id: 'o2',
        name: 'Buy One Get One',
        description: 'Buy one chicken shawarma, get one half price',
        type: 'bogo',
        value: 50,
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isActive: true,
        applicableItems: ['1'],
        usageLimit: 50,
        usedCount: 8
      }
    ]

    setMenuItems(mockMenuItems)
    setOffers(mockOffers)
  }

  const availableCategories = getAllCategoriesFlat().filter(cat => cat.isActive)

  const allowedCategoryIds = selectedCategory === 'all'
    ? null
    : [selectedCategory, ...getCategoryAndDescendantIds(selectedCategory, categories)].map((id) => String(id).trim())

  const filteredItems = menuItems
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (item.tags && item.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()))) ||
                           (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()))
      const rawCategoryId = item.category ?? (item as any).category_id
      const itemCategoryId = rawCategoryId != null && rawCategoryId !== '' ? String(rawCategoryId).trim() : null
      const matchesCategory = selectedCategory === 'all' || (itemCategoryId != null && (allowedCategoryIds?.includes(itemCategoryId) ?? false))
      const matchesStatus =
        filterActiveStatus === 'all' ||
        (filterActiveStatus === 'active' && item.isActive) ||
        (filterActiveStatus === 'inactive' && !item.isActive)
      return matchesSearch && matchesCategory && matchesStatus
    })
    .sort((a, b) => {
      if (sortBy === 'default') return compareProductCode(a.code, b.code)
      if (sortBy === 'price_asc') return (a.basePrice ?? 0) - (b.basePrice ?? 0)
      if (sortBy === 'price_desc') return (b.basePrice ?? 0) - (a.basePrice ?? 0)
      if (sortBy === 'name_asc') return (a.name ?? '').localeCompare(b.name ?? '')
      if (sortBy === 'name_desc') return (b.name ?? '').localeCompare(a.name ?? '')
      return 0
    })

  const handleAddItem = async () => {
    if (newItem.name && newItem.category && newItem.basePrice) {
      try {
        const menuItemData = {
          name: newItem.name!,
          code: newItem.code || null,
          name_ar: newItem.nameAr || null,
          description: newItem.description || '',
          description_ar: newItem.descriptionAr || null,
          category_id: newItem.category!,
          subcategory: newItem.subcategory,
          base_price: newItem.basePrice!,
          image: newItem.image,
          is_active: newItem.isActive ?? true,
          is_popular: newItem.isPopular ?? false,
          preparation_time: newItem.preparationTime || 10,
          calories: newItem.calories,
          steps_to_burn: newItem.stepsToBurn ?? null,
          allergens: newItem.allergens || [],
          tags: newItem.tags || [],
          variants: newItem.variants || [],
          addons: newItem.addons || [],
          upsells: newItem.upsells || [],
          cross_sells: newItem.crossSells || [],
          kds_station_id: newItem.kdsStationId
        }

        const response = await apiService.createMenuItem(menuItemData)
        if (response.success && response.data) {
          // Transform the API response to match frontend format
          const transformedItem = {
            ...(response.data as any),
            nameAr: (response.data as any).name_ar || undefined,
            descriptionAr: (response.data as any).description_ar || undefined,
            category: (response.data as any).category_id, // Convert category_id to category
            basePrice: (response.data as any).base_price, // Convert base_price to basePrice
            preparationTime: (response.data as any).preparation_time, // Convert preparation_time to preparationTime
            calories: (response.data as any).calories,
            stepsToBurn: (response.data as any).steps_to_burn,
            isActive: (response.data as any).is_active, // Convert is_active to isActive
            isPopular: (response.data as any).is_popular, // Convert is_popular to isPopular
            kdsStationId: (response.data as any).kds_station_id, // Convert kds_station_id to kdsStationId
            createdAt: (response.data as any).created_at, // Convert created_at to createdAt
            updatedAt: (response.data as any).updated_at, // Convert updated_at to updatedAt
            // Keep other fields as they are
          } as MenuItem
          setMenuItems([...menuItems, transformedItem])
          setNewItem({})
          setShowAddModal(false)
        } else {
          console.error('Failed to create menu item:', response.error)
          showAlert('Error', 'Failed to create menu item. Please try again.', 'error')
        }
      } catch (error) {
        console.error('Error creating menu item:', error)
        showAlert('Error', 'Error creating menu item. Please try again.', 'error')
      }
    }
  }

  const handleEditItem = async () => {

    
    if (selectedItem && newItem.name && newItem.category && newItem.basePrice) {
      try {
        // Only send image when user changed it. If unchanged, omit so backend keeps current image.
        const originalImage = editModalOriginalImageRef.current
        const imageChanged = (newItem.image as string) !== originalImage
        const imagePayload =
          !imageChanged
            ? undefined
            : newItem.image
              ? imageToApiValue(newItem.image as string)
              : (originalImage ? null : undefined)
        const menuItemData = {
          name: newItem.name!,
          code: newItem.code || null,
          name_ar: newItem.nameAr || null,
          description: newItem.description || '',
          description_ar: newItem.descriptionAr || null,
          category_id: newItem.category!,
          subcategory: newItem.subcategory,
          base_price: newItem.basePrice!,
          ...(imagePayload !== undefined && { image: imagePayload }),
          is_active: newItem.isActive ?? true,
          is_popular: newItem.isPopular ?? false,
          preparation_time: newItem.preparationTime || 10,
          calories: newItem.calories,
          steps_to_burn: newItem.stepsToBurn ?? null,
          allergens: newItem.allergens || [],
          tags: newItem.tags || [],
          variants: newItem.variants || [],
          addons: newItem.addons || [],
          upsells: newItem.upsells || [],
          cross_sells: newItem.crossSells || [],
          kds_station_id: newItem.kdsStationId
        }


        
        const response = await apiService.updateMenuItem(selectedItem.id, menuItemData)

        
        if (response.success && response.data) {
          const resData = response.data as any
          // Transform the API response to match frontend format; resolve image so list display works
          const transformedItem = {
            ...resData,
            image: resolveImageUrl(resData.image),
            nameAr: resData.name_ar || undefined,
            descriptionAr: resData.description_ar || undefined,
            category: resData.category_id,
            basePrice: resData.base_price,
            preparationTime: resData.preparation_time,
            calories: resData.calories,
            stepsToBurn: resData.steps_to_burn,
            isActive: resData.is_active,
            isPopular: resData.is_popular,
            kdsStationId: resData.kds_station_id,
            createdAt: resData.created_at,
            updatedAt: resData.updated_at,
          } as MenuItem
          
          setMenuItems(menuItems.map(item => item.id === selectedItem.id ? transformedItem : item))
          setSelectedItem(null)
          setNewItem({})
          setShowEditModal(false)
          showAlert('Menu Item Updated', 'Menu item updated successfully.', 'success')
        } else {
          console.error('Failed to update menu item:', response.error)
          // Fallback to local update if API fails
          const updatedItem = {
            ...selectedItem,
            name: newItem.name!,
            description: newItem.description || '',
            category: newItem.category!,
            subcategory: newItem.subcategory,
            basePrice: newItem.basePrice!,
            image: newItem.image,
            isActive: newItem.isActive ?? true,
            isPopular: newItem.isPopular ?? false,
            preparationTime: newItem.preparationTime || 10,
            calories: newItem.calories,
            stepsToBurn: newItem.stepsToBurn,
            allergens: newItem.allergens || [],
            tags: newItem.tags || [],
            variants: newItem.variants || [],
            addons: newItem.addons || [],
            upsells: newItem.upsells || [],
            crossSells: newItem.crossSells || [],
            updatedAt: new Date()
          }
          setMenuItems(menuItems.map(item => item.id === selectedItem.id ? updatedItem : item))
          setSelectedItem(null)
          setNewItem({})
          setShowEditModal(false)
          // Show a friendly message to the user
          showAlert('Menu Item Updated', 'Menu item updated in the list, but could not be saved to the database. Please try again.', 'error')
        }
      } catch (error) {
        console.error('Error updating menu item:', error)
        // Fallback to local update if API fails
        const updatedItem = {
          ...selectedItem,
          name: newItem.name!,
          description: newItem.description || '',
          category: newItem.category!,
          subcategory: newItem.subcategory,
          basePrice: newItem.basePrice!,
          image: newItem.image,
          isActive: newItem.isActive ?? true,
          isPopular: newItem.isPopular ?? false,
          preparationTime: newItem.preparationTime || 10,
          calories: newItem.calories,
          stepsToBurn: newItem.stepsToBurn,
          allergens: newItem.allergens || [],
          tags: newItem.tags || [],
          variants: newItem.variants || [],
          addons: newItem.addons || [],
          upsells: newItem.upsells || [],
          crossSells: newItem.crossSells || [],
          kdsStationId: newItem.kdsStationId,
          updatedAt: new Date()
        }
        setMenuItems(menuItems.map(item => item.id === selectedItem.id ? updatedItem : item))
        setSelectedItem(null)
        setNewItem({})
        setShowEditModal(false)
        showAlert('Menu Item Updated', 'Menu item updated in the list, but could not be saved to the database. Please try again.', 'error')
      }
    } else {

      showAlert('Validation', 'Please fill in all required fields (Name, Category, Price)', 'error')
    }
  }

  const openDeleteModal = (item: MenuItem) => {
    setItemToDelete(item)
    setShowDeleteModal(true)
  }

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return
    try {
      const response = await apiService.deleteMenuItem(itemToDelete.id)
      if (response.success) {
        setMenuItems(menuItems.filter(item => item.id !== itemToDelete.id))
        setShowDeleteModal(false)
        setItemToDelete(null)
        setShowDeleteSuccessModal(true)
      } else {
        console.error('Failed to delete menu item:', response.error)
        showAlert('Error', response.error || 'Failed to delete menu item. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Error deleting menu item:', error)
      const message = error instanceof Error ? error.message : 'Failed to delete menu item. Please try again.'
      showAlert('Error', message, 'error')
    }
  }

  const openEditModal = (item: MenuItem) => {
    setSelectedItem(item)
    setImagePreviewBlobUrl(null)
    const img = (item.image as string) ?? ''
    editModalOriginalImageRef.current = img || undefined
    setNewItem({
      name: item.name,
      code: item.code ?? '',
      nameAr: item.nameAr,
      description: item.description,
      descriptionAr: item.descriptionAr,
      category: item.category,
      subcategory: item.subcategory,
      basePrice: item.basePrice,
      image: img,
      isActive: item.isActive,
      isPopular: item.isPopular,
      preparationTime: item.preparationTime,
      calories: item.calories,
      stepsToBurn: item.stepsToBurn,
      allergens: item.allergens,
      tags: item.tags,
      variants: item.variants,
      addons: item.addons,
      upsells: item.upsells,
      crossSells: item.crossSells,
      kdsStationId: item.kdsStationId
    })
    setShowEditModal(true)
  }

  const handleAddAddon = async () => {

    
    const hasValidAddonPrice = newAddon.price !== undefined && newAddon.price !== null && newAddon.price >= 0
    if (newAddon.name && hasValidAddonPrice && newAddon.category) {
      let updatedAddons: Addon[]
      
      if (editingAddon) {
        // Editing existing addon
        const addon: Addon = {
          id: editingAddon.id,
          name: newAddon.name!,
          price: newAddon.price!,
          category: newAddon.category!,
          isRequired: newAddon.isRequired ?? false,
          maxSelection: newAddon.maxSelection,
          kdsStationId: newAddon.kdsStationId,
          allergen_ids: Array.isArray(newAddon.allergen_ids) ? newAddon.allergen_ids : editingAddon.allergen_ids || []
        }
        
        updatedAddons = selectedItem?.addons?.map(a => 
          a.id === editingAddon.id ? addon : a
        ) || []
      } else {
        // Adding new addon
        const addon: Addon = {
          id: Date.now().toString(),
          name: newAddon.name!,
          price: newAddon.price!,
          category: newAddon.category!,
          isRequired: newAddon.isRequired ?? false,
          maxSelection: newAddon.maxSelection,
          kdsStationId: newAddon.kdsStationId,
          allergen_ids: Array.isArray(newAddon.allergen_ids) ? newAddon.allergen_ids : []
        }
        
        updatedAddons = [...(selectedItem?.addons || []), addon as Addon]
      }
      

      
      if (selectedItem) {
        // Update the selectedItem and newItem to reflect the change in the modal
        const updatedSelectedItem = {
          ...selectedItem,
          addons: updatedAddons
        }
        setSelectedItem(updatedSelectedItem)
        setNewItem(updatedSelectedItem)
        
        // Update the global menu items list
        const updatedMenuItems = menuItems.map(item => 
          item.id === selectedItem.id 
            ? { ...item, addons: updatedAddons }
            : item
        )
        setMenuItems(updatedMenuItems)
        


        
        // Save to database immediately
        try {
          const menuItemData = {
            name: updatedSelectedItem.name,
            description: updatedSelectedItem.description || '',
            category_id: updatedSelectedItem.category,
            subcategory: updatedSelectedItem.subcategory,
            base_price: updatedSelectedItem.basePrice,
            is_active: updatedSelectedItem.isActive ?? true,
            is_popular: updatedSelectedItem.isPopular ?? false,
            preparation_time: updatedSelectedItem.preparationTime || 10,
            calories: updatedSelectedItem.calories,
            allergens: updatedSelectedItem.allergens || [],
            tags: updatedSelectedItem.tags || [],
            variants: updatedSelectedItem.variants || [],
            addons: updatedSelectedItem.addons || [],
            upsells: updatedSelectedItem.upsells || [],
            cross_sells: updatedSelectedItem.crossSells || [],
            kds_station_id: updatedSelectedItem.kdsStationId
          }



          const response = await apiService.updateMenuItem(selectedItem.id, menuItemData)


          if (response.success && response.data) {

            showAlert('Addon Saved', 'Addon added and saved to database.', 'success')
          } else {
            console.error('Failed to save addon to database:', response.error)
            showAlert('Addon Not Saved', 'Could not save addon to database. Please try again.', 'error')
          }
        } catch (error) {
          console.error('Error saving addon to database:', error)
          showAlert('Addon Not Saved', 'Could not save addon to database. Please try again.', 'error')
        }
      }
      
      setNewAddon({})
      setEditingAddon(null)
      setShowAddonModal(false)

    } else {

      showAlert('Validation', 'Please fill in all required fields (Name, Price, Category)', 'error')
    }
  }

  const handleAddVariant = async () => {

    
    if (newVariant.name && newVariant.priceModifier !== undefined) {
      let updatedVariants: MenuVariant[]
      
      if (editingVariant) {
        // Editing existing variant
        const variant: MenuVariant = {
          id: editingVariant.id,
          name: newVariant.name!,
          priceModifier: newVariant.priceModifier!,
          isDefault: newVariant.isDefault ?? false,
          allergen_ids: Array.isArray(newVariant.allergen_ids) ? newVariant.allergen_ids : editingVariant.allergen_ids || []
        }
        
        // If this variant is set as default, make all other variants non-default
        updatedVariants = selectedItem?.variants?.map(v => 
          v.id === editingVariant.id ? variant : { ...v, isDefault: v.id === editingVariant.id ? variant.isDefault : (variant.isDefault ? false : v.isDefault) }
        ) || []
        
        // If setting as default, unset others
        if (variant.isDefault) {
          updatedVariants = updatedVariants.map(v => 
            v.id === variant.id ? variant : { ...v, isDefault: false }
          )
        }
      } else {
        // Adding new variant
        const variant: MenuVariant = {
          id: Date.now().toString(),
          name: newVariant.name!,
          priceModifier: newVariant.priceModifier!,
          isDefault: newVariant.isDefault ?? false,
          allergen_ids: Array.isArray(newVariant.allergen_ids) ? newVariant.allergen_ids : []
        }
        
        // If this variant is set as default, make all other variants non-default
        updatedVariants = selectedItem?.variants?.map(v => ({ ...v, isDefault: false })) || []
        updatedVariants.push(variant)
      }
      

      
      if (selectedItem) {
        
        // Update the selectedItem and newItem to reflect the change in the modal
        const updatedSelectedItem = {
          ...selectedItem,
          variants: updatedVariants
        }
        setSelectedItem(updatedSelectedItem)
        setNewItem(updatedSelectedItem)
        
        // Update the global menu items list
        const updatedMenuItems = menuItems.map(item => 
          item.id === selectedItem.id 
            ? { ...item, variants: updatedVariants }
            : item
        )
        setMenuItems(updatedMenuItems)
        


        
        // Save to database immediately
        try {
          const menuItemData = {
            name: updatedSelectedItem.name,
            description: updatedSelectedItem.description || '',
            category_id: updatedSelectedItem.category,
            subcategory: updatedSelectedItem.subcategory,
            base_price: updatedSelectedItem.basePrice,
            is_active: updatedSelectedItem.isActive ?? true,
            is_popular: updatedSelectedItem.isPopular ?? false,
            preparation_time: updatedSelectedItem.preparationTime || 10,
            calories: updatedSelectedItem.calories,
            allergens: updatedSelectedItem.allergens || [],
            tags: updatedSelectedItem.tags || [],
            variants: updatedSelectedItem.variants || [],
            addons: updatedSelectedItem.addons || [],
            upsells: updatedSelectedItem.upsells || [],
            cross_sells: updatedSelectedItem.crossSells || []
          }



          const response = await apiService.updateMenuItem(selectedItem.id, menuItemData)


          if (response.success && response.data) {

            showAlert('Variant Saved', 'Variant added and saved to database.', 'success')
          } else {
            console.error('Failed to save variant to database:', response.error)
            showAlert('Variant Not Saved', 'Could not save variant to database. Please try again.', 'error')
          }
        } catch (error) {
          console.error('Error saving variant to database:', error)
          showAlert('Variant Not Saved', 'Could not save variant to database. Please try again.', 'error')
        }
      }
      
      setNewVariant({})
      setEditingVariant(null)
      setShowVariantModal(false)

    } else {

      showAlert('Validation', 'Please fill in all required fields (Name, Price Modifier)', 'error')
    }
  }

  const handleAddOffer = async () => {
    const offerType = newOffer.type ?? selectedOfferType
    // Basic validation (use offerType so default dropdown selection counts)
    if (!newOffer.name?.trim() || !offerType) {
      showAlert('Validation', 'Please fill in Offer Name and Offer Type', 'error')
      return
    }

    // Type-specific validation
    if (offerType === 'percentage' || offerType === 'fixed') {
      if (!newOffer.value || newOffer.value <= 0) {
        showAlert('Validation', 'Please enter a valid discount value', 'error')
        return
      }
    } else if (offerType === 'combo') {
      if (comboItems.length === 0) {
        showAlert('Validation', 'Please add at least one combo item', 'error')
        return
      }
      // Validate combo items
      for (const item of comboItems) {
        if (!item.id || !item.name || item.price <= 0) {
          showAlert('Validation', 'Please complete all combo items (select item and set price)', 'error')
          return
        }
      }
    } else if (offerType === 'bogo') {
      if (bogoItems.length === 0) {
        showAlert('Validation', 'Please add at least one BOGO rule', 'error')
        return
      }
      // Validate BOGO items
      for (const item of bogoItems) {
        if (!item.buyItemId || !item.getItemId) {
          showAlert('Validation', 'Please complete all BOGO rules (select buy item and get item)', 'error')
          return
        }
      }
    }

    try {
      // Prepare offer data based on type (use offerType so default dropdown counts)
      let offerData: any = {
        name: newOffer.name!.trim(),
        description: newOffer.description || '',
        type: offerType,
        value: newOffer.value || 0, // Default to 0 for combo/bogo
        start_date: newOffer.startDate || new Date(),
        end_date: newOffer.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        is_active: newOffer.isActive ?? true,
        min_order_amount: newOffer.minOrderAmount,
        max_discount: newOffer.maxDiscount,
        usage_limit: newOffer.usageLimit,
        used_count: 0
      }

      // Add type-specific data
      if (offerType === 'percentage' || offerType === 'fixed') {
        offerData.applicable_items = selectedMenuItems.length > 0 ? selectedMenuItems : []
      } else if (offerType === 'combo') {
        offerData.combo_items = comboItems
      } else if (offerType === 'bogo') {
        offerData.bogo_items = bogoItems
      }


      const response = await apiService.createOffer(offerData)

      
      if (response.success && response.data) {
        // Transform the API response to match frontend format
        const transformedOffer = {
          ...(response.data as any),
          isActive: (response.data as any).is_active, // Convert is_active to isActive
          startDate: new Date((response.data as any).start_date), // Convert start_date to startDate
          endDate: new Date((response.data as any).end_date), // Convert end_date to endDate
          minOrderAmount: (response.data as any).min_order_amount, // Convert min_order_amount to minOrderAmount
          maxDiscount: (response.data as any).max_discount, // Convert max_discount to maxDiscount
          usageLimit: (response.data as any).usage_limit, // Convert usage_limit to usageLimit
          usedCount: (response.data as any).used_count, // Convert used_count to usedCount
          applicableItems: (response.data as any).applicable_items, // Convert applicable_items to applicableItems
          comboItems: (response.data as any).combo_items || [], // Map combo_items -> comboItems
          bogoItems: (response.data as any).bogo_items || [], // Map bogo_items -> bogoItems
          createdAt: (response.data as any).created_at, // Convert created_at to createdAt
          updatedAt: (response.data as any).updated_at, // Convert updated_at to updatedAt
        } as Offer & { createdAt?: any; updatedAt?: any }
        setOffers([...offers, transformedOffer as Offer]);
        setNewOffer({});
        setShowOfferModal(false);
      } else {
        console.error('Failed to create offer:', response.error);
        // Fallback to local state if API fails (table doesn't exist yet)
        const localOffer: Offer = {
          id: Date.now().toString(),
          name: newOffer.name!,
          description: newOffer.description || '',
          type: newOffer.type!,
          value: newOffer.value!,
          startDate: newOffer.startDate || new Date(),
          endDate: newOffer.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          isActive: newOffer.isActive ?? true,
          applicableItems: newOffer.applicableItems || [],
          minOrderAmount: newOffer.minOrderAmount,
          maxDiscount: newOffer.maxDiscount,
          usageLimit: newOffer.usageLimit,
          usedCount: 0
        }
        setOffers([...offers, localOffer]);
        setNewOffer({});
        setShowOfferModal(false);
        showAlert('Offer Created', 'Offer created successfully.', 'success')
      }
    } catch (error) {
      console.error('Error creating offer:', error);
      showAlert('Error', 'Failed to create offer. Please try again.', 'error')
    }
  }

  const handleViewOffer = (offer: Offer) => {
    setSelectedOffer(offer)
    setShowViewOfferModal(true)
  }

  const handleEditOffer = (offer: Offer) => {
    setSelectedOffer(offer)
    setNewOffer({
      name: offer.name,
      description: offer.description,
      type: offer.type,
      value: offer.value,
      startDate: offer.startDate,
      endDate: offer.endDate,
      isActive: offer.isActive,
      minOrderAmount: offer.minOrderAmount,
      maxDiscount: offer.maxDiscount,
      usageLimit: offer.usageLimit,
      applicableItems: offer.applicableItems || []
    })
    setSelectedOfferType(offer.type)
    setSelectedMenuItems(offer.applicableItems || [])
    setComboItems(offer.comboItems || [])
    setBogoItems(offer.bogoItems || [])
    setShowEditOfferModal(true)
  }

  const handleUpdateOffer = async () => {
    if (!selectedOffer || !newOffer.name || !newOffer.type) {
      showAlert('Validation', 'Please fill in Offer Name and Offer Type', 'error')
      return
    }

    try {
      // Prepare offer data based on type
      let offerData: any = {
        name: newOffer.name!,
        description: newOffer.description || '',
        type: newOffer.type!,
        value: newOffer.value || 0,
        start_date: newOffer.startDate || new Date(),
        end_date: newOffer.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        is_active: newOffer.isActive ?? true,
        min_order_amount: newOffer.minOrderAmount,
        max_discount: newOffer.maxDiscount,
        usage_limit: newOffer.usageLimit,
        used_count: selectedOffer.usedCount
      }

      // Add type-specific data
      if (newOffer.type === 'percentage' || newOffer.type === 'fixed') {
        offerData.applicable_items = selectedMenuItems.length > 0 ? selectedMenuItems : []
      } else if (newOffer.type === 'combo') {
        offerData.combo_items = comboItems
      } else if (newOffer.type === 'bogo') {
        offerData.bogo_items = bogoItems
      }


      const response = await apiService.updateOffer(selectedOffer.id, offerData)

      
      if (response.success && response.data) {
        // Transform the API response to match frontend format
        const data = response.data as any
        const transformedOffer: Offer = {
          id: selectedOffer.id,
          name: data.name ?? selectedOffer.name,
          description: data.description ?? selectedOffer.description ?? '',
          type: (data.type ?? selectedOffer.type) as Offer['type'],
          value: data.value != null ? Number(data.value) : selectedOffer.value,
          startDate: data.start_date ? new Date(data.start_date) : selectedOffer.startDate,
          endDate: data.end_date ? new Date(data.end_date) : selectedOffer.endDate,
          isActive: data.is_active ?? selectedOffer.isActive,
          minOrderAmount: data.min_order_amount != null ? Number(data.min_order_amount) : selectedOffer.minOrderAmount,
          maxDiscount: data.max_discount != null ? Number(data.max_discount) : selectedOffer.maxDiscount,
          usageLimit: data.usage_limit != null ? Number(data.usage_limit) : selectedOffer.usageLimit,
          usedCount: data.used_count != null ? Number(data.used_count) : selectedOffer.usedCount,
          applicableItems: Array.isArray(data.applicable_items) ? data.applicable_items : (selectedOffer.applicableItems || []),
          comboItems: Array.isArray(data.combo_items) ? data.combo_items : (selectedOffer.comboItems || []),
          bogoItems: Array.isArray(data.bogo_items) ? data.bogo_items : (selectedOffer.bogoItems || []),
        }
        
        // Update the offers list using functional updater to avoid stale state
        setOffers(prev => prev.map(offer =>
          offer.id === selectedOffer.id ? transformedOffer : offer
        ))
        
        setShowEditOfferModal(false)
        setSelectedOffer(null)
        setNewOffer({})
        setShowOfferUpdateSuccessModal(true)
      } else {
        console.error('Failed to update offer:', response.error)
        showAlert('Error', 'Failed to update offer. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Error updating offer:', error)
      showAlert('Error', 'Failed to update offer. Please try again.', 'error')
    }
  }

  const handleDeleteOfferClick = (offerId: string) => {
    showConfirm('Delete Offer', 'Are you sure you want to delete this offer?', () => performDeleteOffer(offerId))
  }

  const performDeleteOffer = async (offerId: string) => {
    try {
      const response = await apiService.deleteOffer(offerId)
      if (response.success) {
        setOffers(prev => prev.filter(offer => offer.id !== offerId))
        showAlert('Offer Deleted', 'The offer has been deleted successfully.', 'success')
      } else {
        console.error('Failed to delete offer:', response.error)
        showAlert('Error', response.error || 'Failed to delete offer. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Error deleting offer:', error)
      showAlert('Error', 'Failed to delete offer. Please try again.', 'error')
    }
  }

  const toggleItemStatus = (itemId: string) => {
    setMenuItems(menuItems.map(item => 
      item.id === itemId ? { ...item, isActive: !item.isActive } : item
    ))
  }

  const toggleItemPopular = (itemId: string) => {
    setMenuItems(menuItems.map(item => 
      item.id === itemId ? { ...item, isPopular: !item.isPopular } : item
    ))
  }

  const toggleBulkSelectItem = (itemId: string) => {
    setSelectedItemIds(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    )
  }

  const toggleBulkSelectAll = () => {
    if (selectedItemIds.length >= filteredItems.length) {
      setSelectedItemIds([])
    } else {
      setSelectedItemIds(filteredItems.map(item => item.id))
    }
  }

  const clearBulkSelection = () => {
    setSelectedItemIds([])
  }

  const handleBulkDelete = () => {
    const count = selectedItemIds.length
    showConfirm(
      'Delete selected items',
      `Are you sure you want to delete ${count} item(s)? This cannot be undone.`,
      async () => {
        try {
          for (const id of selectedItemIds) {
            const response = await apiService.deleteMenuItem(id)
            if (!response.success) throw new Error(response.error)
          }
          setMenuItems(menuItems.filter(item => !selectedItemIds.includes(item.id)))
          setSelectedItemIds([])
          showAlert('Success', `${count} item(s) deleted.`, 'success')
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Failed to delete some items.'
          showAlert('Error', msg, 'error')
        }
      }
    )
  }

  const handleBulkSetActive = async (active: boolean) => {
    try {
      for (const id of selectedItemIds) {
        const response = await apiService.updateMenuItem(id, { is_active: active })
        if (!response.success) throw new Error((response as any).error)
      }
      setMenuItems(menuItems.map(item =>
        selectedItemIds.includes(item.id) ? { ...item, isActive: active } : item
      ))
      setSelectedItemIds([])
      showAlert('Success', `Selected item(s) marked as ${active ? 'active' : 'inactive'}.`, 'success')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update some items.'
      showAlert('Error', msg, 'error')
    }
  }

  // const deleteItem = (itemId: string) => {
  //   if (confirm('Are you sure you want to delete this menu item?')) {
  //     setMenuItems(menuItems.filter(item => item.id !== itemId))
  //   }
  // }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Menu Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your menu items, addons, offers, and promotions
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => { setNewItem(initialNewItem); setImagePreviewBlobUrl(null); setShowAddModal(true) }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { key: 'items', label: 'Menu Items', icon: ShoppingCartIcon },
              { key: 'offers', label: 'Offers & Promotions', icon: TagIcon },
              { key: 'upsells', label: 'Upselling', icon: StarIcon },
              { key: 'addonCategories', label: 'Addon Categories', icon: AdjustmentsHorizontalIcon }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <tab.icon className="mr-2 h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Menu Items Tab */}
          {activeTab === 'items' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search menu items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[160px]"
                >
                  <option value="all">All Categories</option>
                  {availableCategories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <select
                  value={filterActiveStatus}
                  onChange={(e) => setFilterActiveStatus(e.target.value as 'all' | 'active' | 'inactive')}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[140px]"
                >
                  <option value="all">All status</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'default' | 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc')}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[160px]"
                >
                  <option value="default">Sort by</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="name_asc">Name: A–Z</option>
                  <option value="name_desc">Name: Z–A</option>
                </select>
                {filteredItems.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={selectedItemIds.length === filteredItems.length && filteredItems.length > 0}
                      onChange={toggleBulkSelectAll}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                    Select all ({filteredItems.length})
                  </label>
                )}
              </div>

              {/* Bulk actions bar */}
              {selectedItemIds.length > 0 && (
                <div className="flex items-center justify-between gap-4 flex-wrap rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-4 py-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {selectedItemIds.length} item(s) selected
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleBulkSetActive(true)}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                    >
                      Make active
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkSetActive(false)}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
                    >
                      Make inactive
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkDelete}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={clearBulkSelection}
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      Clear selection
                    </button>
                  </div>
                </div>
              )}

              {/* Menu Items Grid - 4 columns on xl */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {menuItemsLoading ? (
                  /* Shimmer loading placeholders */
                  Array.from({ length: 8 }).map((_, i) => (
                    <div key={`shimmer-${i}`} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden animate-pulse">
                      <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                      <div className="p-4 space-y-3">
                        <div className="flex justify-between gap-2">
                          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded flex-1 max-w-[70%]" />
                          <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-[80%]" />
                        </div>
                        <div className="flex justify-between gap-2">
                          <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                          <div className="h-4 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
                        </div>
                        <div className="flex gap-2">
                          <div className="h-6 w-14 bg-gray-200 dark:bg-gray-700 rounded-full" />
                          <div className="h-6 w-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
                          <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                        </div>
                        <div className="flex justify-between pt-2">
                          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                          <div className="flex gap-1">
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                filteredItems.map(item => (
                  <div key={item.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow relative">
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedItemIds.includes(item.id)}
                        onChange={() => toggleBulkSelectItem(item.id)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        aria-label={`Select ${item.name}`}
                      />
                    </div>
                    <div className="aspect-square bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{item.name}</h3>
                          {item.code && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{item.code}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          {item.isPopular && (
                            <StarIcon className="h-4 w-4 text-yellow-500" />
                          )}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.isActive 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {item.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-3">
                        <span className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {item.preparationTime} min
                        </span>
                        <span className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          {formatCurrency(item.basePrice || 0)}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1 mb-3">
                        {(item.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {tag}
                          </span>
                        ))}
                        {(item.tags || []).length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            +{(item.tags || []).length - 3}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.variants?.length || 0} variants • {item.addons?.length || 0} addons
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleItemPopular(item.id)}
                            className={`p-1 rounded ${
                              item.isPopular 
                                ? 'text-yellow-500 hover:text-yellow-600' 
                                : 'text-gray-400 hover:text-yellow-500'
                            }`}
                            title={item.isPopular ? 'Remove from popular' : 'Mark as popular'}
                          >
                            <StarIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggleItemStatus(item.id)}
                            className={`p-1 rounded ${
                              item.isActive 
                                ? 'text-green-500 hover:text-green-600' 
                                : 'text-gray-400 hover:text-green-500'
                            }`}
                            title={item.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-1 rounded text-blue-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(item)}
                            className="p-1 rounded text-red-500 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
                )}
              </div>
            </div>
          )}

          {/* Offers Tab */}
          {activeTab === 'offers' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Offers & Promotions
                </h3>
                <button
                  onClick={() => {
                    setNewOffer({ type: selectedOfferType })
                    setShowOfferModal(true)
                  }}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create Offer
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {offers.map(offer => (
                  <div key={offer.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{offer.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.description}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        offer.isActive 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {offer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Type:</span>
                        <span className="font-medium text-gray-900 dark:text-white capitalize">
                          {offer.type === 'bogo' ? 'Buy One Get One' : offer.type}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Value:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatOfferValue(offer)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Usage:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {offer.usedCount}/{offer.usageLimit || '∞'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Valid until: {offer.endDate.toLocaleDateString()}
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewOffer(offer)}
                          className="text-green-600 hover:text-green-500 text-sm font-medium"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleEditOffer(offer)}
                          className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteOfferClick(offer.id)}
                          className="text-red-600 hover:text-red-500 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upselling Tab */}
          {activeTab === 'upsells' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Upselling & Cross-selling
              </h3>
              
              <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6">
                <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
                  Smart Upselling Rules
                </h4>
                <p className="text-blue-800 dark:text-blue-300 text-sm mb-4">
                  Configure automatic suggestions based on order value, customer history, and popular combinations.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Order Value Triggers</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Suggest add-ons when order reaches certain thresholds
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Customer History</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Recommend items based on previous orders
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Popular Combinations</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Suggest items that are frequently ordered together
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-2">Time-based Offers</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Show different suggestions based on time of day
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Addon Categories Tab */}
          {activeTab === 'addonCategories' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Addon Categories</h3>
                <button
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                  onClick={() => setShowAddCategoryModal(true)}
                >Add Category</button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="space-y-2">
                  {addonCategories.map(cat => (
                    <div key={cat.id} className="flex items-center">
                      <input
                        className="flex-1 mr-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        value={cat.name}
                        onChange={(e) => {
                          const name = e.target.value
                          setAddonCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name } : c))
                        }}
                        onBlur={async (e) => {
                          const name = e.target.value.trim()
                          if (!name) return
                          const resp = await apiService.updateAddonCategory(cat.id, { name })
                          if (!resp.success) {
                            showAlert('Error', resp.error || 'Failed to update category', 'error')
                          }
                        }}
                      />
                      <button
                        className="ml-2 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 hover:text-red-700"
                        title="Delete category"
                        aria-label="Delete category"
                        onClick={() => {
                          showConfirm('Delete Category', 'Are you sure you want to delete this category?', () => {
                            apiService.deleteAddonCategory(cat.id).then(resp => {
                              if (resp.success) {
                                setAddonCategories(prev => prev.filter(c => c.id !== cat.id))
                              } else {
                                showAlert('Error', resp.error || 'Failed to delete category', 'error')
                              }
                            }).catch(() => showAlert('Error', 'Failed to delete category', 'error'))
                          })
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {addonCategories.length === 0 && (
                    <div className="text-sm text-gray-500">No categories yet</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Menu Item Modal */}
      {showAddAddonCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Addon Category</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category Name *</label>
                <input
                  type="text"
                  value={newAddonCategoryName}
                  onChange={(e) => setNewAddonCategoryName(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Protein"
                />
              </div>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowAddAddonCategoryModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >Cancel</button>
              <button
                onClick={async () => {
                  const name = newAddonCategoryName.trim()
                  if (!name) return
                  const resp = await apiService.createAddonCategory({ name })
                  if (resp.success && resp.data) {
                    setAddonCategories(prev => [...prev, resp.data as { id: string; name: string; is_active: boolean; sort_order: number }])
                    setShowAddAddonCategoryModal(false)
                    setNewAddonCategoryName('')
                  } else {
                    showAlert('Error', resp.error || 'Failed to create category', 'error')
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Menu Item Modal */}
      {showAddModal && (
        
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Menu Item
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Code
                </label>
                <input
                  type="text"
                  value={newItem.code || ''}
                  onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white max-w-xs"
                  placeholder="e.g. A01, A02"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={newItem.name || ''}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter item name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={newItem.category || ''}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {availableCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Name (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newItem.nameAr || ''}
                  onChange={(e) => setNewItem({ ...newItem, nameAr: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="اسم الصنف (اختياري)"
                  dir="rtl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newItem.description || ''}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the item..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={newItem.descriptionAr || ''}
                  onChange={(e) => setNewItem({ ...newItem, descriptionAr: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="وصف الصنف (اختياري)"
                  dir="rtl"
                />
              </div>

              

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image
                </label>
                {(newItem.image || imagePreviewBlobUrl) && (
                  <div className="mb-2 flex items-center gap-2" key={`edit-preview-${selectedItem?.id ?? 'new'}-${imagePreviewBlobUrl || (newItem.image as string) || ''}`}>
                    <div className="h-32 w-32 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0 relative">
                      <img
                        src={imagePreviewBlobUrl || resolveImageUrl(newItem.image as string)}
                        alt="Preview"
                        className="h-full w-full object-cover"
                        onLoad={(e) => {
                          const el = e.target as HTMLImageElement
                          el.style.display = ''
                          const fallback = el.parentElement?.querySelector('.image-preview-fallback') as HTMLElement
                          if (fallback) fallback.classList.add('hidden')
                        }}
                        onError={(e) => {
                          const el = e.target as HTMLImageElement
                          el.onerror = null
                          el.style.display = 'none'
                          const fallback = el.parentElement?.querySelector('.image-preview-fallback') as HTMLElement
                          if (fallback) fallback.classList.remove('hidden')
                        }}
                      />
                      <span className="image-preview-fallback hidden absolute inset-0 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 p-2 text-center bg-gray-100 dark:bg-gray-700" aria-hidden>Image unavailable</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const blobUrl = URL.createObjectURL(file)
                      setImagePreviewBlobUrl(blobUrl)
                      try {
                        const res = await apiService.uploadImage(file, 'menu-items')
                        if (res.success && res.url) {
                          setNewItem({ ...newItem, image: res.url })
                          URL.revokeObjectURL(blobUrl)
                          setImagePreviewBlobUrl(null)
                        }
                      } catch {
                        URL.revokeObjectURL(blobUrl)
                        setImagePreviewBlobUrl(null)
                      }
                    }}
                    className="w-full text-sm text-gray-900 dark:text-gray-100"
                  />
                  <input
                    type="url"
                    value={(newItem.image as string) || ''}
                    onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                    placeholder="https://... (optional)"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                {(newItem.image || imagePreviewBlobUrl) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (imagePreviewBlobUrl) {
                        URL.revokeObjectURL(imagePreviewBlobUrl)
                        setImagePreviewBlobUrl(null)
                      }
                      setNewItem({ ...newItem, image: '' })
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-500"
                  >
                    Remove image
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Price *
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(in {restaurantSettings.currency_symbol})</p>
                  <input
                    type="number"
                    step="0.01"
                    value={newItem.basePrice || ''}
                    onChange={(e) => setNewItem({ ...newItem, basePrice: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Preparation Time <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(min)</span>
                  </label>
                  <input
                    type="number"
                    value={newItem.preparationTime || ''}
                    onChange={(e) => setNewItem({ ...newItem, preparationTime: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Calories
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(per serving)</p>
                  <input
                    type="number"
                    value={newItem.calories || ''}
                    onChange={(e) => setNewItem({ ...newItem, calories: e.target.value === '' ? undefined : Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Steps to burn
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(calories)</p>
                  <input
                    type="number"
                    value={newItem.stepsToBurn ?? ''}
                    onChange={(e) => setNewItem({ ...newItem, stepsToBurn: e.target.value === '' ? undefined : Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allergens</label>
                <div className="flex flex-wrap gap-2">
                  {allergensList.map((a) => {
                    const selected = Array.isArray(newItem.allergens) && newItem.allergens.includes(a.id)
                    return (
                      <label key={a.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = Array.isArray(newItem.allergens) ? newItem.allergens : []
                            if (e.target.checked) setNewItem({ ...newItem, allergens: [...current, a.id] })
                            else setNewItem({ ...newItem, allergens: current.filter((id) => id !== a.id) })
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {a.icon && <img src={resolveImageUrl(a.icon)} alt="" className="w-5 h-5 object-contain" />}
                        <span className="text-sm text-gray-900 dark:text-white">{a.name}</span>
                      </label>
                    )
                  })}
                  {allergensList.length === 0 && <span className="text-sm text-gray-500 dark:text-gray-400">No allergens defined. Add them in Settings → Allergens.</span>}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                onClick={() => { setShowAddModal(false); setNewItem(initialNewItem) }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddItem}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Add Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Menu Item Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Menu Item
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Code
                </label>
                <input
                  type="text"
                  value={newItem.code || ''}
                  onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white max-w-xs"
                  placeholder="e.g. A01, A02"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Item Name *
                  </label>
                  <input
                    type="text"
                    value={newItem.name || ''}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter item name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={newItem.category || ''}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {availableCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Item Name (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newItem.nameAr || ''}
                  onChange={(e) => setNewItem({ ...newItem, nameAr: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="اسم الصنف (اختياري)"
                  dir="rtl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newItem.description || ''}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the item..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={newItem.descriptionAr || ''}
                  onChange={(e) => setNewItem({ ...newItem, descriptionAr: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="وصف الصنف (اختياري)"
                  dir="rtl"
                />
              </div>

              {/* Image Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image
                </label>
                {(newItem.image || imagePreviewBlobUrl) && (
                  <div className="mb-2 flex items-center gap-2" key={`add-preview-${imagePreviewBlobUrl || (newItem.image as string) || ''}`}>
                    <div className="h-32 w-32 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0 relative">
                      <img
                        src={imagePreviewBlobUrl || resolveImageUrl(newItem.image as string)}
                        alt="Preview"
                        className="h-full w-full object-cover"
                        onLoad={(e) => {
                          const el = e.target as HTMLImageElement
                          el.style.display = ''
                          const fallback = el.parentElement?.querySelector('.image-preview-fallback') as HTMLElement
                          if (fallback) fallback.classList.add('hidden')
                        }}
                        onError={(e) => {
                          const el = e.target as HTMLImageElement
                          el.onerror = null
                          el.style.display = 'none'
                          const fallback = el.parentElement?.querySelector('.image-preview-fallback') as HTMLElement
                          if (fallback) fallback.classList.remove('hidden')
                        }}
                      />
                      <span className="image-preview-fallback hidden absolute inset-0 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 p-2 text-center bg-gray-100 dark:bg-gray-700" aria-hidden>Image unavailable</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Upload image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        const blobUrl = URL.createObjectURL(file)
                        setImagePreviewBlobUrl(blobUrl)
                        try {
                          const res = await apiService.uploadImage(file, 'menu-items')
                          if (res.success && res.url) {
                            setNewItem({ ...newItem, image: res.url })
                            URL.revokeObjectURL(blobUrl)
                            setImagePreviewBlobUrl(null)
                          }
                        } catch {
                          URL.revokeObjectURL(blobUrl)
                          setImagePreviewBlobUrl(null)
                        }
                      }}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Or image URL
                    </label>
                    <input
                      type="text"
                      value={(newItem.image as string) || ''}
                      onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
                {newItem.image && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setNewItem({ ...newItem, image: '' })}
                      className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Remove image
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Price *
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(in {restaurantSettings.currency_symbol})</p>
                  <input
                    type="number"
                    step="0.01"
                    value={newItem.basePrice || ''}
                    onChange={(e) => setNewItem({ ...newItem, basePrice: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Preparation Time <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">(min)</span>
                  </label>
                  <input
                    type="number"
                    value={newItem.preparationTime || ''}
                    onChange={(e) => setNewItem({ ...newItem, preparationTime: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Calories
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(per serving)</p>
                  <input
                    type="number"
                    value={newItem.calories || ''}
                    onChange={(e) => setNewItem({ ...newItem, calories: e.target.value === '' ? undefined : Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Steps to burn
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">(calories)</p>
                  <input
                    type="number"
                    value={newItem.stepsToBurn ?? ''}
                    onChange={(e) => setNewItem({ ...newItem, stepsToBurn: e.target.value === '' ? undefined : Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allergens</label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Select allergens for this menu item (icons will show in POS, Waiter, customer menu)</p>
                <div className="flex flex-wrap gap-2">
                  {allergensList.map((a) => {
                    const selected = Array.isArray(newItem.allergens) && newItem.allergens.includes(a.id)
                    return (
                      <label key={a.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = Array.isArray(newItem.allergens) ? newItem.allergens : []
                            if (e.target.checked) setNewItem({ ...newItem, allergens: [...current, a.id] })
                            else setNewItem({ ...newItem, allergens: current.filter((id) => id !== a.id) })
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {a.icon && <img src={resolveImageUrl(a.icon)} alt="" className="w-5 h-5 object-contain" />}
                        <span className="text-sm text-gray-900 dark:text-white">{a.name}</span>
                      </label>
                    )
                  })}
                  {allergensList.length === 0 && <span className="text-sm text-gray-500 dark:text-gray-400">No allergens defined. Add them in Settings → Allergens.</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newItem.isActive ?? true}
                    onChange={(e) => setNewItem({ ...newItem, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-white">
                    Active
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPopular"
                    checked={newItem.isPopular ?? false}
                    onChange={(e) => setNewItem({ ...newItem, isPopular: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isPopular" className="ml-2 block text-sm text-gray-900 dark:text-white">
                    Popular Item
                  </label>
                </div>
              </div>
            </div>

            {/* Variants Management Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                  Variants & Options
                </h4>
                <button
                  onClick={() => setShowVariantModal(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                >
                  <PlusIcon className="-ml-1 mr-1 h-4 w-4" />
                  Add Variant
                </button>
              </div>
              
              {selectedItem?.variants && selectedItem.variants.length > 0 ? (
                <div className="space-y-2">
                  {selectedItem.variants.map(variant => (
                    <div key={variant.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            {variant.name}
                            {variant.isDefault && (
                              <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                                Default
                              </span>
                            )}
                          </h5>
                          <span className={`text-sm font-medium ${
                            variant.priceModifier >= 0 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {variant.priceModifier >= 0 ? '+' : ''}{formatCurrency(variant.priceModifier)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-3">
                        {/* Recipe Management Button for Variant */}
                        {posSettings.linkInventoryWithProducts && selectedItem?.id && variant.id && (
                          <button
                            onClick={() => handleManageRecipe('variant', selectedItem.id, variant.id)}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                            title="Manage Recipe"
                          >
                            <AdjustmentsHorizontalIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingVariant(variant)
                            setNewVariant({
                              name: variant.name,
                              priceModifier: variant.priceModifier,
                              isDefault: variant.isDefault,
                              allergen_ids: variant.allergen_ids || []
                            })
                            setShowVariantModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit Variant"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            const updatedItem = {
                              ...selectedItem,
                              variants: selectedItem.variants?.map(v => 
                                v.id === variant.id ? { ...v, isDefault: true } : { ...v, isDefault: false }
                              ) || []
                            }
                            
                            // Update the global menu items list
                            setMenuItems(menuItems.map(item => 
                              item.id === selectedItem.id ? updatedItem : item
                            ))
                            
                            // Update the selectedItem and newItem to reflect the change in the modal
                            setSelectedItem(updatedItem)
                            setNewItem(updatedItem)
                          }}
                          className={`text-xs px-2 py-1 rounded ${
                            variant.isDefault 
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' 
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900'
                          }`}
                          disabled={variant.isDefault}
                        >
                          {variant.isDefault ? 'Default' : 'Set Default'}
                        </button>
                        <button
                          onClick={async () => {
                            const updatedItem = {
                              ...selectedItem,
                              variants: selectedItem.variants?.filter(v => v.id !== variant.id) || []
                            }
                            
                            // Update the global menu items list
                            setMenuItems(menuItems.map(item => 
                              item.id === selectedItem.id ? updatedItem : item
                            ))
                            
                            // Update the selectedItem and newItem to reflect the change in the modal
                            setSelectedItem(updatedItem)
                            setNewItem(updatedItem)
                            
                            // Save to database immediately
                            try {
                              const menuItemData = {
                                name: updatedItem.name,
                                description: updatedItem.description || '',
                                category_id: updatedItem.category,
                                subcategory: updatedItem.subcategory,
                                base_price: updatedItem.basePrice,
                                is_active: updatedItem.isActive ?? true,
                                is_popular: updatedItem.isPopular ?? false,
                                preparation_time: updatedItem.preparationTime || 10,
                                calories: updatedItem.calories,
                                allergens: updatedItem.allergens || [],
                                tags: updatedItem.tags || [],
                                variants: updatedItem.variants || [],
                                addons: updatedItem.addons || [],
                                upsells: updatedItem.upsells || [],
                                cross_sells: updatedItem.crossSells || []
                              }

                              const response = await apiService.updateMenuItem(selectedItem.id, menuItemData)
                              if (response.success && response.data) {

                              } else {
                                showAlert('Could Not Save', 'Could not save changes to database. Please try again.', 'error')
                              }
                            } catch (error) {
                              console.error('Error saving variant deletion to database:', error)
                              showAlert('Could Not Save', 'Could not save changes to database. Please try again.', 'error')
                            }
                          }}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <AdjustmentsHorizontalIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No variants added yet</p>
                  <p className="text-sm">Click "Add Variant" to create size/option variations for this item</p>
                </div>
              )}
            </div>

            {/* Addons Management Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                  Addons & Modifiers
                </h4>
                <button
                  onClick={() => setShowAddonModal(true)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <PlusIcon className="-ml-1 mr-1 h-4 w-4" />
                  Add Addon
                </button>
              </div>
              
              {selectedItem?.addons && selectedItem.addons.length > 0 ? (
                <div className="space-y-2">
                  {selectedItem.addons.map(addon => (
                    <div key={addon.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900 dark:text-white">{addon.name}</h5>
                          <span className="text-sm font-medium text-green-600 dark:text-green-400">
                            +{formatCurrency(addon.price)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>Category: {addon.category}</span>
                          <div className="flex items-center space-x-2">
                            <span>{addon.isRequired ? 'Required' : 'Optional'}</span>
                            {addon.maxSelection && (
                              <span>Max: {addon.maxSelection}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Recipe Management Button for Addon */}
                        {posSettings.linkInventoryWithProducts && selectedItem?.id && addon.id && (
                          <button
                            onClick={() => handleManageRecipe('addon', selectedItem.id, undefined, addon.id)}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                            title="Manage Recipe"
                          >
                            <AdjustmentsHorizontalIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingAddon(addon)
                            setNewAddon({
                              name: addon.name,
                              price: addon.price,
                              category: addon.category,
                              isRequired: addon.isRequired,
                              maxSelection: addon.maxSelection,
                              kdsStationId: addon.kdsStationId,
                              allergen_ids: addon.allergen_ids || []
                            })
                            setShowAddonModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit Addon"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={async () => {
                            const updatedItem = {
                              ...selectedItem,
                              addons: selectedItem.addons?.filter(a => a.id !== addon.id) || []
                            }
                          
                          // Update the global menu items list
                          setMenuItems(menuItems.map(item => 
                            item.id === selectedItem.id ? updatedItem : item
                          ))
                          
                          // Update the selectedItem and newItem to reflect the change in the modal
                          setSelectedItem(updatedItem)
                          setNewItem(updatedItem)
                          
                          // Save to database immediately
                          try {
                            const menuItemData = {
                              name: updatedItem.name,
                              description: updatedItem.description || '',
                              category_id: updatedItem.category,
                              subcategory: updatedItem.subcategory,
                              base_price: updatedItem.basePrice,
                              is_active: updatedItem.isActive ?? true,
                              is_popular: updatedItem.isPopular ?? false,
                              preparation_time: updatedItem.preparationTime || 10,
                              calories: updatedItem.calories,
                              allergens: updatedItem.allergens || [],
                              tags: updatedItem.tags || [],
                              variants: updatedItem.variants || [],
                              addons: updatedItem.addons || [],
                              upsells: updatedItem.upsells || [],
                              cross_sells: updatedItem.crossSells || []
                            }



                            const response = await apiService.updateMenuItem(selectedItem.id, menuItemData)


                            if (response.success && response.data) {

                            } else {
                              console.error('Failed to save addon deletion to database:', response.error)
                              showAlert('Could Not Save', 'Could not save changes to database. Please try again.', 'error')
                            }
                          } catch (error) {
                            console.error('Error saving addon deletion to database:', error)
                            showAlert('Could Not Save', 'Could not save changes to database. Please try again.', 'error')
                          }
                        }}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <AdjustmentsHorizontalIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No addons added yet</p>
                  <p className="text-sm">Click "Add Addon" to create modifiers for this item</p>
                </div>
              )}
            </div>

              {/* Recipe Management Button - Only show if setting is enabled */}
              {posSettings.linkInventoryWithProducts && selectedItem?.id && (
                <div className="mb-4">
                  <button
                    onClick={() => handleManageRecipe('item', selectedItem.id)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                  >
                    <AdjustmentsHorizontalIcon className="w-5 h-5 mr-2" />
                    Manage Recipe
                  </button>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => { setShowEditModal(false); setSelectedItem(null); setNewItem(initialNewItem) }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditItem}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Update Item
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Add/Edit Variant Modal */}
      {showVariantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingVariant ? 'Edit Variant' : 'Add Variant'} {selectedItem?.name ? `to ${selectedItem.name}` : ''}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Variant Name *
                </label>
                <input
                  type="text"
                  value={newVariant.name || ''}
                  onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Small, Medium, Large"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Price Modifier *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">{restaurantSettings.currency_symbol}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={newVariant.priceModifier || ''}
                    onChange={(e) => setNewVariant({ ...newVariant, priceModifier: Number(e.target.value) })}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Positive values add to base price, negative values subtract
                </p>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={newVariant.isDefault || false}
                  onChange={(e) => setNewVariant({ ...newVariant, isDefault: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Set as default variant
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allergens (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {allergensList.map((a) => {
                    const selected = Array.isArray(newVariant.allergen_ids) && newVariant.allergen_ids.includes(a.id)
                    return (
                      <label key={a.id} className="inline-flex items-center gap-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = Array.isArray(newVariant.allergen_ids) ? newVariant.allergen_ids : []
                            if (e.target.checked) setNewVariant({ ...newVariant, allergen_ids: [...current, a.id] })
                            else setNewVariant({ ...newVariant, allergen_ids: current.filter((id) => id !== a.id) })
                          }}
                          className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {a.icon && <img src={resolveImageUrl(a.icon)} alt="" className="w-4 h-4 object-contain" />}
                        <span className="text-gray-900 dark:text-white">{a.name}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowVariantModal(false)
                    setEditingVariant(null)
                    setNewVariant({})
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              <button
                onClick={handleAddVariant}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingVariant ? 'Update Variant' : 'Add Variant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Addon Modal */}
      {showAddonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingAddon ? 'Edit Addon' : 'Add Addon'} {selectedItem?.name ? `to ${selectedItem.name}` : ''}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Addon Name *
                </label>
                <input
                  type="text"
                  value={newAddon.name || ''}
                  onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter addon name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newAddon.price ?? ''}
                    onChange={(e) => setNewAddon({
                      ...newAddon,
                      price: e.target.value === '' ? undefined : Number(e.target.value)
                    })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={newAddon.category || ''}
                    onChange={(e) => setNewAddon({ ...newAddon, category: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {addonCategories.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    KDS Station
                  </label>
                  <select
                    value={newAddon.kdsStationId || ''}
                    onChange={(e) => setNewAddon({ ...newAddon, kdsStationId: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select KDS Station</option>
                    {kdsStations.map(station => (
                      <option key={station.id} value={station.id}>{station.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newAddon.isRequired || false}
                    onChange={(e) => setNewAddon({ ...newAddon, isRequired: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allergens (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {allergensList.map((a) => {
                    const selected = Array.isArray(newAddon.allergen_ids) && newAddon.allergen_ids.includes(a.id)
                    return (
                      <label key={a.id} className="inline-flex items-center gap-2 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = Array.isArray(newAddon.allergen_ids) ? newAddon.allergen_ids : []
                            if (e.target.checked) setNewAddon({ ...newAddon, allergen_ids: [...current, a.id] })
                            else setNewAddon({ ...newAddon, allergen_ids: current.filter((id) => id !== a.id) })
                          }}
                          className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {a.icon && <img src={resolveImageUrl(a.icon)} alt="" className="w-4 h-4 object-contain" />}
                        <span className="text-gray-900 dark:text-white">{a.name}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddonModal(false)
                    setEditingAddon(null)
                    setNewAddon({})
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAddon}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {editingAddon ? 'Update Addon' : 'Add Addon'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Offer Modal */}
      {showOfferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Create New Offer
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Offer Name *
                </label>
                <input
                  type="text"
                  value={newOffer.name || ''}
                  onChange={(e) => setNewOffer({ ...newOffer, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter offer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newOffer.description || ''}
                  onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the offer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Offer Type *
                </label>
                <select
                  value={selectedOfferType}
                  onChange={(e) => {
                    setSelectedOfferType(e.target.value as any)
                    setNewOffer({ ...newOffer, type: e.target.value as any })
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="percentage">Percentage Discount</option>
                  <option value="fixed">Fixed Amount Discount</option>
                  <option value="bogo">Buy One Get One</option>
                  <option value="combo">Combo Deal</option>
                </select>
              </div>

              {/* Offer Type Specific Fields */}
              {selectedOfferType === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newOffer.value || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, value: Number(e.target.value) })}
                      className="w-full pr-8 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="20"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">%</span>
                  </div>
                </div>
              )}

              {selectedOfferType === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">{restaurantSettings.currency_symbol}</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOffer.value || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, value: Number(e.target.value) })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="5.00"
                    />
                  </div>
                </div>
              )}

              {selectedOfferType === 'bogo' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    BOGO Configuration
                  </label>
                  {bogoItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Buy Item
                          </label>
                          <select
                            value={item.buyItemId}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].buyItemId = e.target.value
                              updated[index].buyItemName = menuItems.find(m => m.id === e.target.value)?.name || ''
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Get Item
                          </label>
                          <select
                            value={item.getItemId}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].getItemId = e.target.value
                              updated[index].getItemName = menuItems.find(m => m.id === e.target.value)?.name || ''
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Discount Type
                          </label>
                          <select
                            value={item.discountType}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].discountType = e.target.value as any
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="free">Free</option>
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Discount Value
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.discountValue}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].discountValue = Number(e.target.value)
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              const updated = bogoItems.filter((_, i) => i !== index)
                              setBogoItems(updated)
                            }}
                            className="w-full bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-2 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setBogoItems([...bogoItems, {
                        buyItemId: '',
                        buyItemName: '',
                        getItemId: '',
                        getItemName: '',
                        discountType: 'free',
                        discountValue: 0
                      }])
                    }}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-2 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                  >
                    + Add BOGO Item
                  </button>
                </div>
              )}

              {selectedOfferType === 'combo' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Combo Items
                  </label>
                  {comboItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Menu Item
                          </label>
                          <select
                            value={item.id}
                            onChange={(e) => {
                              const updated = [...comboItems]
                              updated[index].id = e.target.value
                              updated[index].name = menuItems.find(m => m.id === e.target.value)?.name || ''
                              updated[index].price = menuItems.find(m => m.id === e.target.value)?.basePrice || 0
                              setComboItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Combo Price
                          </label>
                          <div className="relative">
                            <span className="absolute left-2 top-1 text-gray-500 text-sm">{restaurantSettings.currency_symbol}</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.price}
                              onChange={(e) => {
                                const updated = [...comboItems]
                                updated[index].price = Number(e.target.value)
                                setComboItems(updated)
                              }}
                              className="w-full pl-6 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              const updated = comboItems.filter((_, i) => i !== index)
                              setComboItems(updated)
                            }}
                            className="w-full bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-2 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setComboItems([...comboItems, {
                        id: '',
                        name: '',
                        price: 0
                      }])
                    }}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-2 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                  >
                    + Add Combo Item
                  </button>
                </div>
              )}

              {/* Applicable Items for Percentage/Fixed Discounts */}
              {(selectedOfferType === 'percentage' || selectedOfferType === 'fixed') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Applicable Items
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMenuItems.length === menuItems.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMenuItems(menuItems.map(item => item.id))
                            } else {
                              setSelectedMenuItems([])
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          All Menu Items
                        </span>
                      </label>
                      {menuItems.map(item => (
                        <label key={item.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedMenuItems.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMenuItems([...selectedMenuItems, item.id])
                              } else {
                                setSelectedMenuItems(selectedMenuItems.filter(id => id !== item.id))
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-900 dark:text-white">
                            {item.name} - {formatCurrency(item.basePrice)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={newOffer.isActive ?? true}
                  onChange={(e) => setNewOffer({ ...newOffer, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Active Offer
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowOfferModal(false)
                    setNewOffer({})
                    setSelectedMenuItems([])
                    setComboItems([])
                    setBogoItems([])
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddOffer}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Create Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Offer Modal */}
      {showViewOfferModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Offer Details
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Offer Name
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium">{selectedOffer.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedOffer.isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {selectedOffer.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <p className="text-gray-900 dark:text-white">{selectedOffer.description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Offer Type
                  </label>
                  <p className="text-gray-900 dark:text-white capitalize">
                    {selectedOffer.type === 'bogo' ? 'Buy One Get One' : selectedOffer.type}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Value
                  </label>
                  <p className="text-gray-900 dark:text-white">{formatOfferValue(selectedOffer)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <p className="text-gray-900 dark:text-white">{selectedOffer.startDate.toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <p className="text-gray-900 dark:text-white">{selectedOffer.endDate.toLocaleDateString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Usage
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {selectedOffer.usedCount}/{selectedOffer.usageLimit || '∞'}
                  </p>
                </div>
                {selectedOffer.minOrderAmount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Min Order Amount
                    </label>
                    <p className="text-gray-900 dark:text-white">{formatCurrency(selectedOffer.minOrderAmount)}</p>
                  </div>
                )}
              </div>

              {/* Applicable Items */}
              {(selectedOffer.type === 'percentage' || selectedOffer.type === 'fixed') && selectedOffer.applicableItems && selectedOffer.applicableItems.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Applicable Items
                  </label>
                  <div className="space-y-1">
                    {selectedOffer.applicableItems.map(itemId => {
                      const item = menuItems.find(m => m.id === itemId)
                      return item ? (
                        <div key={itemId} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <span className="text-gray-900 dark:text-white">{item.name}</span>
                          <span className="text-gray-600 dark:text-gray-400">{formatCurrency(item.basePrice)}</span>
                        </div>
                      ) : null
                    })}
                  </div>
                </div>
              )}

              {/* Combo Items */}
              {selectedOffer.type === 'combo' && selectedOffer.comboItems && selectedOffer.comboItems.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Combo Items
                  </label>
                  <div className="space-y-1">
                    {selectedOffer.comboItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-gray-900 dark:text-white">{item.name}</span>
                        <span className="text-gray-600 dark:text-gray-400">{formatCurrency(item.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* BOGO Items */}
              {selectedOffer.type === 'bogo' && selectedOffer.bogoItems && selectedOffer.bogoItems.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    BOGO Rules
                  </label>
                  <div className="space-y-2">
                    {selectedOffer.bogoItems.map((item, index) => (
                      <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">Buy:</span>
                            <p className="text-gray-900 dark:text-white">{item.buyItemName}</p>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">Get:</span>
                            <p className="text-gray-900 dark:text-white">{item.getItemName}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Discount:</span>
                          <span className="text-gray-900 dark:text-white">
                            {item.discountType === 'free' ? 'Free' : 
                             item.discountType === 'percentage' ? `${item.discountValue}%` : 
                             formatCurrency(item.discountValue)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowViewOfferModal(false)}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowViewOfferModal(false)
                    handleEditOffer(selectedOffer)
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Edit Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Offer Modal */}
      {showEditOfferModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Offer
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Offer Name *
                </label>
                <input
                  type="text"
                  value={newOffer.name || ''}
                  onChange={(e) => setNewOffer({ ...newOffer, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter offer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newOffer.description || ''}
                  onChange={(e) => setNewOffer({ ...newOffer, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Describe the offer..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Offer Type *
                </label>
                <select
                  value={selectedOfferType}
                  onChange={(e) => {
                    setSelectedOfferType(e.target.value as any)
                    setNewOffer({ ...newOffer, type: e.target.value as any })
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="percentage">Percentage Discount</option>
                  <option value="fixed">Fixed Amount Discount</option>
                  <option value="bogo">Buy One Get One</option>
                  <option value="combo">Combo Deal</option>
                </select>
              </div>

              {/* Offer Type Specific Fields */}
              {selectedOfferType === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newOffer.value || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, value: Number(e.target.value) })}
                      className="w-full pr-8 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="20"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">%</span>
                  </div>
                </div>
              )}

              {selectedOfferType === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">{restaurantSettings.currency_symbol}</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOffer.value || ''}
                      onChange={(e) => setNewOffer({ ...newOffer, value: Number(e.target.value) })}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="5.00"
                    />
                  </div>
                </div>
              )}

              {/* BOGO Configuration */}
              {selectedOfferType === 'bogo' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    BOGO Configuration
                  </label>
                  {bogoItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Buy Item
                          </label>
                          <select
                            value={item.buyItemId}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].buyItemId = e.target.value
                              updated[index].buyItemName = menuItems.find(m => m.id === e.target.value)?.name || ''
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Get Item
                          </label>
                          <select
                            value={item.getItemId}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].getItemId = e.target.value
                              updated[index].getItemName = menuItems.find(m => m.id === e.target.value)?.name || ''
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Discount Type
                          </label>
                          <select
                            value={item.discountType}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].discountType = e.target.value as any
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="free">Free</option>
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Discount Value
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.discountValue}
                            onChange={(e) => {
                              const updated = [...bogoItems]
                              updated[index].discountValue = Number(e.target.value)
                              setBogoItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="0"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              const updated = bogoItems.filter((_, i) => i !== index)
                              setBogoItems(updated)
                            }}
                            className="w-full bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-2 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setBogoItems([...bogoItems, {
                        buyItemId: '',
                        buyItemName: '',
                        getItemId: '',
                        getItemName: '',
                        discountType: 'free',
                        discountValue: 0
                      }])
                    }}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-2 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                  >
                    + Add BOGO Item
                  </button>
                </div>
              )}

              {/* Combo Items */}
              {selectedOfferType === 'combo' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Combo Items
                  </label>
                  {comboItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Menu Item
                          </label>
                          <select
                            value={item.id}
                            onChange={(e) => {
                              const updated = [...comboItems]
                              updated[index].id = e.target.value
                              updated[index].name = menuItems.find(m => m.id === e.target.value)?.name || ''
                              updated[index].price = menuItems.find(m => m.id === e.target.value)?.basePrice || 0
                              setComboItems(updated)
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="">Select item...</option>
                            {menuItems.map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Combo Price
                          </label>
                          <div className="relative">
                            <span className="absolute left-2 top-1 text-gray-500 text-sm">{restaurantSettings.currency_symbol}</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.price}
                              onChange={(e) => {
                                const updated = [...comboItems]
                                updated[index].price = Number(e.target.value)
                                setComboItems(updated)
                              }}
                              className="w-full pl-6 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => {
                              const updated = comboItems.filter((_, i) => i !== index)
                              setComboItems(updated)
                            }}
                            className="w-full bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-2 rounded"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setComboItems([...comboItems, {
                        id: '',
                        name: '',
                        price: 0
                      }])
                    }}
                    className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-2 text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
                  >
                    + Add Combo Item
                  </button>
                </div>
              )}

              {/* Applicable Items for Percentage/Fixed Discounts */}
              {(selectedOfferType === 'percentage' || selectedOfferType === 'fixed') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Applicable Items
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedMenuItems.length === menuItems.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMenuItems(menuItems.map(item => item.id))
                            } else {
                              setSelectedMenuItems([])
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          All Menu Items
                        </span>
                      </label>
                      {menuItems.map(item => (
                        <label key={item.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedMenuItems.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMenuItems([...selectedMenuItems, item.id])
                              } else {
                                setSelectedMenuItems(selectedMenuItems.filter(id => id !== item.id))
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-900 dark:text-white">
                            {item.name} - {formatCurrency(item.basePrice)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={newOffer.isActive ?? true}
                  onChange={(e) => setNewOffer({ ...newOffer, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActiveEdit" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Active Offer
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditOfferModal(false)
                    setSelectedOffer(null)
                    setNewOffer({})
                    setSelectedMenuItems([])
                    setComboItems([])
                    setBogoItems([])
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateOffer}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Update Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Add New Category
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter category name"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddCategoryModal(false)
                  setNewCategoryName('')
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newCategoryName.trim()) return
                  
                  const resp = await apiService.createAddonCategory({ name: newCategoryName.trim() })
                  if (resp.success && resp.data) {
                    setAddonCategories(prev => [...prev, resp.data as { id: string; name: string; is_active: boolean; sort_order: number }])
                    setShowAddCategoryModal(false)
                    setNewCategoryName('')
                  } else {
                    showAlert('Error', resp.error || 'Failed to create category', 'error')
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Management Modal */}
      {showRecipeModal && recipeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Manage Recipe {recipeTarget.type === 'variant' ? '(Variant)' : recipeTarget.type === 'addon' ? '(Addon)' : '(Menu Item)'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Recipe Name (Optional)
                </label>
                <input
                  type="text"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter recipe name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={recipeDescription}
                  onChange={(e) => setRecipeDescription(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter recipe description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Serving Size
                </label>
                <input
                  type="number"
                  min="1"
                  value={recipeServingSize}
                  onChange={(e) => setRecipeServingSize(parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Number of servings this recipe makes. Quantities will be calculated per serving.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ingredients
                  </label>
                  <button
                    onClick={handleAddIngredient}
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                  >
                    + Add Ingredient
                  </button>
                </div>

                {recipeIngredients.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No ingredients added. Click "Add Ingredient" to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {recipeIngredients.map((ingredient, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Inventory Item *
                          </label>
                          <select
                            value={ingredient.inventory_item_id}
                            onChange={(e) => handleUpdateIngredient(index, 'inventory_item_id', e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          >
                            <option value="">Select Item</option>
                            {inventoryItems.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name} ({item.currentStock} {item.unit})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Quantity *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={ingredient.quantity}
                            onChange={(e) => handleUpdateIngredient(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Unit
                          </label>
                          <input
                            type="text"
                            value={ingredient.unit}
                            onChange={(e) => handleUpdateIngredient(index, 'unit', e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            placeholder="kg, pcs, etc."
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Notes
                          </label>
                          <input
                            type="text"
                            value={ingredient.notes || ''}
                            onChange={(e) => handleUpdateIngredient(index, 'notes', e.target.value)}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            placeholder="Optional"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            onClick={() => handleRemoveIngredient(index)}
                            className="w-full bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-sm"
                            title="Remove"
                          >
                            <TrashIcon className="w-4 h-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowRecipeModal(false)
                    setRecipeTarget(null)
                    setRecipeIngredients([])
                    setRecipeName('')
                    setRecipeDescription('')
                    setRecipeServingSize(1)
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveRecipe}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Save Recipe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Menu Item Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Menu Item
            </h3>
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this menu item?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">{itemToDelete.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {itemToDelete.category} • {restaurantSettings.currency_symbol}{itemToDelete.basePrice}
                </p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setItemToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteItem}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircleIcon className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Menu Item Deleted
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              The menu item has been deleted successfully.
            </p>
            <button
              onClick={() => setShowDeleteSuccessModal(false)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Offer Update Success Modal */}
      {showOfferUpdateSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 text-center">
            <div className="flex justify-center mb-4">
              <CheckCircleIcon className="h-16 w-16 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Offer Updated
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              The offer has been updated successfully.
            </p>
            <button
              onClick={() => setShowOfferUpdateSuccessModal(false)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Reusable Alert / Confirm Modal */}
      {alertModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 text-center">
            <div className="flex justify-center mb-4">
              {alertModal.variant === 'success' && <CheckCircleIcon className="h-16 w-16 text-green-500" />}
              {alertModal.variant === 'error' && <XCircleIcon className="h-16 w-16 text-red-500" />}
              {(alertModal.variant === 'info' || alertModal.variant === 'confirm') && (
                <ExclamationTriangleIcon className="h-16 w-16 text-amber-500" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {alertModal.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 whitespace-pre-wrap">
              {alertModal.message}
            </p>
            <div className={`flex gap-3 ${alertModal.confirmOnly ? 'justify-center' : 'justify-end'}`}>
              {!alertModal.confirmOnly && (
                <button
                  onClick={closeAlertModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={alertModal.confirmOnly ? closeAlertModal : handleAlertConfirm}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  alertModal.variant === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : alertModal.variant === 'error'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuManagement
